import React from "react";
import AttachmentPreviewActionHeader from "./components/ActionHeader";
import AttachmentPreviewViewer from "./components";
import { LeftPreviewArrow, RightPreviewArrow } from "app/components/Icons";
import type { TicketAttachmentFile } from "app/containers/Features/containers/Ticket/types";
import CustomTooltip from "app/components/Tooltip";

export type AttachmentPreviewProps = {
  fileName?: string;
  fileType?: TicketAttachmentFile["type"];
  fileUrl?: string;
  loading?: boolean;
  onDownload?: () => void;
  onOpenFile?: () => void;
  onCopyUrl?: () => void;
  onDelete?: () => void;
  onClose?: () => void;
  onRenameSubmit?: (name: string) => void;
  zoomLevel?: number;
  onZoomOut?: () => void;
  onZoomIn?: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
  currentItemNumber?: number;
  totalItems?: number;
};

const AttachmentPreview: React.FC<AttachmentPreviewProps> = ({
  fileName,
  fileType,
  fileUrl,
  loading = false,
  onDownload,
  onOpenFile,
  onCopyUrl,
  onDelete,
  onClose,
  onRenameSubmit,
  zoomLevel,
  onZoomOut,
  onZoomIn,
  onPrevious,
  onNext,
  currentItemNumber,
  totalItems,
}) => {
  const [internalZoomLevel, setInternalZoomLevel] = React.useState(100);
  const resolvedZoomLevel = zoomLevel ?? internalZoomLevel;

  const handleInternalZoomIn = React.useCallback(() => {
    setInternalZoomLevel((prevZoom) => Math.min(prevZoom + 10, 400));
  }, []);

  const handleInternalZoomOut = React.useCallback(() => {
    setInternalZoomLevel((prevZoom) => Math.max(prevZoom - 10, 25));
  }, []);

  const handleZoomIn = onZoomIn ?? handleInternalZoomIn;
  const handleZoomOut = onZoomOut ?? handleInternalZoomOut;

  React.useEffect(() => {
    setInternalZoomLevel(100);
  }, [fileUrl]);

  return (
    <div className="h-full min-h-0 flex flex-col bg-white">
      <AttachmentPreviewActionHeader
        fileName={fileName}
        onDownload={onDownload}
        onOpenFile={onOpenFile}
        onCopyUrl={onCopyUrl}
        onDelete={onDelete}
        onClose={onClose}
        onRenameSubmit={onRenameSubmit}
        zoomLevel={resolvedZoomLevel}
        onZoomOut={handleZoomOut}
        onZoomIn={handleZoomIn}
        currentItemNumber={currentItemNumber}
        totalItems={totalItems}
      />
      <div className="flex-1 min-h-0 relative">
        <AttachmentPreviewViewer
          fileType={fileType}
          fileUrl={fileUrl}
          fileName={fileName}
          zoomLevel={resolvedZoomLevel}
          loading={loading}
        />

        <div className="absolute left-4 right-4 top-1/2 -translate-y-1/2 h-[72px] flex items-center justify-between pointer-events-none">
          <button
            type="button"
            onClick={onPrevious}
            className="h-[72px] w-[72px] flex items-center justify-center focus:outline-none pointer-events-auto disabled:opacity-50"
            disabled={!onPrevious}
            aria-label="Previous attachment"
          >
            <CustomTooltip text="Go to previous file" position="bottom">
              <LeftPreviewArrow size={72} color="#79808A" />
            </CustomTooltip>
          </button>
          <button
            type="button"
            onClick={onNext}
            className="h-[72px] w-[72px] flex items-center justify-center focus:outline-none pointer-events-auto disabled:opacity-50"
            disabled={!onNext}
            aria-label="Next attachment"
          >
            <CustomTooltip text="Go to next file" position="bottom">
              <RightPreviewArrow size={72} color="#79808A" />
            </CustomTooltip>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AttachmentPreview;